import { Editor } from '@ckeditor/ckeditor5-core/src/editor/editor';
import { getEnvKeystrokeText } from '@ckeditor/ckeditor5-utils/src/keyboard';
import { Popover } from 'antd';
import classNames from 'classnames/bind';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { TranslationKey, useTranslation } from 'hooks/use-translation';
import styles from './styles.module.css';

const getKeyStroke = (keyStroke: string): string[] => {
  const key = keyStroke && getEnvKeystrokeText(keyStroke);

  return key.split('');
};

type PopoverPositions =
  | 'bottom'
  | 'top'
  | 'left'
  | 'right'
  | 'topLeft'
  | 'topRight'
  | 'bottomLeft'
  | 'bottomRight'
  | 'leftTop'
  | 'leftBottom'
  | 'rightTop'
  | 'rightBottom'
  | undefined;

interface Props {
  children: React.ReactNode;
  command: {
    [key: string]: any;
  };
  editor: Editor;
  label: string;
  className?: string;
  classNameEnabled?: string;
  keyStroke?: string;
  popoverPosition?: PopoverPositions;
  haveItsOwnHandler: boolean | undefined;
  commandName: string;
  commandValue?: string;
  haveActiveState: boolean;
  hideTooltip?: boolean;
  enabled?: boolean;
}

export const ToolButton: FC<Props> = ({
  children,
  command,
  editor,
  label = '',
  keyStroke,
  className,
  classNameEnabled,
  popoverPosition = 'bottom',
  haveItsOwnHandler,
  commandName,
  commandValue = '',
  haveActiveState,
  hideTooltip = false,
  enabled = false,
}: Props) => {
  const [isEnabled, setEnabled] = useState(enabled);
  const [activeClass, setActiveClass] = useState(false);
  const onIsEnabledChange = useCallback(() => {
    setEnabled(command?.isEnabled);
  }, [command]);
  const onValueChange = useCallback(() => {
    setActiveClass(command.value);
  }, [command]);

  useEffect(() => {
    command?.on('change:isEnabled', onIsEnabledChange);
    if (!enabled) {
      onIsEnabledChange();
    }
    if (haveActiveState && command) {
      command.on('change:value', onValueChange);
      onValueChange();
    }
  }, [enabled, command, haveActiveState, onIsEnabledChange, onValueChange]);

  const handleClick = () => {
    if (!haveItsOwnHandler) {
      editor?.execute(commandName, { value: commandValue });
    }
  };

  const translatedLabel = useTranslation(label as TranslationKey);
  const keyStrokeLabel = keyStroke ? getKeyStroke(keyStroke) : [];

  const tooltipContent = label ? (
    <div className={styles.tooltip__content}>
      <p>{translatedLabel}</p>
      {keyStrokeLabel.length > 1 ? (
        <p className={styles.key}>
          {keyStrokeLabel.map((key) => (
            <span key={key}>{key}</span>
          ))}
        </p>
      ) : null}
    </div>
  ) : null;

  const btnClasses = classNames(styles.button, className, {
    [styles.active]: activeClass,
    [styles.enabled]: isEnabled,
    [`${classNameEnabled}`]: isEnabled,
  });

  return haveItsOwnHandler ? (
    <Popover
      placement={popoverPosition}
      content={tooltipContent}
      overlayClassName={`${!hideTooltip ? styles.tooltip : 'hide'}`}
    >
      <div data-testid={commandName} className={btnClasses}>
        {children}
      </div>
    </Popover>
  ) : (
    <Popover
      placement={popoverPosition}
      content={tooltipContent}
      overlayClassName={`${styles.tooltip}`}
    >
      <div>
        <button
          type="button"
          data-testid={commandName}
          disabled={!isEnabled}
          className={btnClasses}
          onClick={handleClick}
          onMouseDown={(e) => e.preventDefault()}
        >
          {children}
        </button>
      </div>
    </Popover>
  );
};
